/**
 * PlayerScoresPage.jsx
 * All players with their W/L record.
 * Clicking a player expands a full match history drill-down across all sports.
 * Doubles matches show both partner names.
 */
import { useEffect, useState, useCallback } from 'react';
import { Users, RefreshCw, Search, X, ChevronDown, ChevronUp, Clock } from 'lucide-react';
import { downloadCSV, printPage } from '@/lib/exportUtils';
import ExportBar from '@/components/ExportBar';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import useAuthStore from '@/stores/authStore';
import { toast } from 'sonner';
import SportIcon, { SPORT_LABELS } from '@/components/SportIcon';

const RESULT_CONFIG = {
  win:  { label: 'W', color: '#22c55e', bg: 'rgba(34,197,94,0.1)'  },
  loss: { label: 'L', color: '#ef4444', bg: 'rgba(239,68,68,0.1)'  },
  draw: { label: 'D', color: '#f5c518', bg: 'rgba(245,197,24,0.1)' },
  bye:  { label: 'BYE', color: '#8a93a3', bg: 'rgba(138,147,163,0.1)' },
};

const RESULT_SPORTS = ['chess', 'carrom'];

const fmtDate = (iso) => iso
  ? new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', timeZone: 'Asia/Kolkata' })
  : '';

// ── Individual match row — clickable to expand details ─────────
function MatchRow({ m }) {
  const [expanded, setExpanded] = useState(false);
  const rc         = RESULT_CONFIG[m.result] || RESULT_CONFIG.loss;
  const isResult   = RESULT_SPORTS.includes(m.sport_code);
  const DOUBLES_SPORTS = new Set(['badminton', 'carrom', 'pickleball']);
  const isDoubles  = m.match_type?.toLowerCase() === 'doubles' ||
                     DOUBLES_SPORTS.has(m.sport_code) ||   // These sports are all-doubles
                     (m.opp_players?.length > 1) ||
                     (m.my_players?.length > 1);
  const isSportSummary = m.match_type === 'sport_summary';

  const oppLabel = (() => {
    const opp = m.opp_player || m.opp_team || 'Opponent';
    if (!isDoubles) return opp;
    const oppPlayers = m.opp_players || [];
    if (oppPlayers.length > 1) return oppPlayers.join(' / ');
    return opp;
  })();

  const myPartner = (() => {
    if (!isDoubles) return null;
    const myPlayers = m.my_players || [];
    if (myPlayers.length > 1) return myPlayers.find(p => p !== m.my_player) || null;
    return null;
  })();

  const scoreStr = isResult ? null : (m.my_score != null && m.opp_score != null ? `${m.my_score}–${m.opp_score}` : null);
  const games    = (m.games || []).filter(g => g.home || g.away);
  const scoreLabel = isResult
    ? (m.result === 'win' ? 'Win' : m.result === 'loss' ? 'Loss' : 'Draw')
    : scoreStr;

  return (
    <div className="border-b border-[#1e2435]/60 last:border-0">
      {/* Clickable row */}
      <button className="w-full text-left flex items-center gap-3 px-3 py-2.5 hover:bg-[#161a26] transition-colors"
        onClick={() => setExpanded(e => !e)}>
        {/* Result badge */}
        <div className="h-9 w-9 rounded-xl flex-shrink-0 flex items-center justify-center font-black text-xs border-2"
          style={{ color: rc.color, borderColor: `${rc.color}40`, backgroundColor: rc.bg }}>
          {rc.label}
        </div>

        {/* Sport icon + slot */}
        <div className="flex-shrink-0 text-center w-10">
          <SportIcon sport={m.sport_code} size={20}/>
          {!isSportSummary && <p className="text-[8px] text-[#8a93a3] font-mono mt-0.5">{m.slot || 'M'}</p>}
          {isDoubles && <p className="text-[7px] text-[#f5c518] font-bold">DBL</p>}
        </div>

        {/* Opponent info */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-[#f0f2f7] truncate">vs {oppLabel}</p>
          {myPartner && <p className="text-[10px] text-[#f5c518] truncate">w/ {myPartner} (partner)</p>}
          <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
            {m.opp_team && oppLabel !== m.opp_team && <span className="text-[9px] text-[#8a93a3]">{m.opp_team}</span>}
            {m.match_label && <span className="text-[9px] font-bold text-[#f5c518] bg-[rgba(245,197,24,0.1)] px-1 rounded">{m.match_label}</span>}
            {m.scheduled_at && <span className="text-[9px] text-[#8a93a3] flex items-center gap-0.5"><Clock className="h-2 w-2"/>{fmtDate(m.scheduled_at)}</span>}
          </div>
        </div>

        {/* Score + chevron */}
        <div className="text-right flex-shrink-0">
          {scoreLabel && <p className="text-sm font-black tabular-nums" style={{ color: rc.color }}>{scoreLabel}</p>}
          {!isResult && games.length > 0 && (
            <p className="text-[9px] text-[#8a93a3] font-mono">{games.map(g => `${g.home}-${g.away}`).join(' ')}</p>
          )}
        </div>
        <ChevronDown className={`h-3.5 w-3.5 text-[#8a93a3] flex-shrink-0 ml-1 transition-transform ${expanded ? 'rotate-180' : ''}`}/>
      </button>

      {/* Expanded detail */}
      {expanded && (
        <div className="px-3 pb-3 bg-[#0a0c13] border-t border-[#1e2435]/40 space-y-2 pt-2">
          {/* Headline — show both partners for doubles */}
          <div className="flex items-center justify-between gap-2 py-2 px-3 bg-[#161a26] rounded-xl">
            <div className="flex-1 text-center min-w-0">
              <p className={`text-sm font-black truncate ${m.result==='win'?'text-[#22c55e]':'text-[#f0f2f7]'}`}>
                {m.my_player || 'Me'}
              </p>
              {/* Show doubles partner on my side */}
              {isDoubles && (
                <p className="text-[10px] text-[#8a93a3] mt-0.5">
                  {m.my_players?.filter(p => p && p !== m.my_player).length > 0
                    ? `& ${m.my_players.filter(p => p && p !== m.my_player)[0]}`
                    : '& (partner not recorded)'}
                </p>
              )}
              {m.result==='win' && <p className="text-[9px] text-[#22c55e] font-bold">🏆 Winner</p>}
            </div>
            <div className="flex-shrink-0 px-3 text-center">
              {scoreLabel
                ? <p className="text-lg font-black tabular-nums" style={{ color: rc.color }}>{scoreLabel}</p>
                : <p className="text-[#8a93a3] text-sm">–</p>}
              <p className="text-[9px] text-[#8a93a3]">{SPORT_LABELS[m.sport_code]||m.sport_code}</p>
            </div>
            <div className="flex-1 text-center min-w-0">
              <p className={`text-sm font-black truncate ${m.result==='loss'?'text-[#22c55e]':'text-[#f0f2f7]'}`}>
                {isDoubles && m.opp_players?.length > 1
                  ? m.opp_players[0]
                  : oppLabel}
              </p>
              {/* Show doubles partner on opponent side */}
              {isDoubles && (
                <p className="text-[10px] text-[#8a93a3] mt-0.5">
                  {(m.opp_players||[]).length > 1
                    ? `& ${m.opp_players[1]}`
                    : '& (partner not recorded)'}
                </p>
              )}
              {m.result==='loss' && <p className="text-[9px] text-[#22c55e] font-bold">🏆 Winner</p>}
            </div>
          </div>
          {/* Game-by-game (TT) */}
          {games.length > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-[9px] text-[#8a93a3] uppercase tracking-wide">Games:</span>
              {games.map((g, gi) => (
                <span key={gi} className="text-[10px] font-mono bg-[#1d2230] border border-[#252a37] px-1.5 py-0.5 rounded text-[#f0f2f7]">
                  {g.home}–{g.away}
                </span>
              ))}
            </div>
          )}
          {/* Meta */}
          <div className="flex items-center justify-between text-[9px] text-[#8a93a3]">
            <span className="truncate">{m.tournament_name}</span>
            <span className="flex items-center gap-1 flex-shrink-0 ml-2">
              {m.match_label && <span className="font-mono text-[#f5c518]">{m.match_label}</span>}
              {m.scheduled_at && <span className="flex items-center gap-0.5"><Clock className="h-2 w-2"/>{fmtDate(m.scheduled_at)}</span>}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
// ── Player drill-down ───────────────────────────────────────────
function PlayerDrilldown({ player, orgSlug, token }) {
  const [matchData, setMatchData] = useState(null);
  const [loading, setLoading]     = useState(true);
  const [sportFilter, setSportFilter] = useState('all');

  useEffect(() => {
    fetch(`/api/orgs/${orgSlug}/players/${player.player_id}/matches`,
      { headers: { Authorization: `Bearer ${token()}` } })
      .then(r => r.ok ? r.json() : Promise.reject(r))
      .then(d => setMatchData(d))
      .catch(() => toast.error('Failed to load matches'))
      .finally(() => setLoading(false));
  }, [player.player_id, orgSlug]); // eslint-disable-line react-hooks/exhaustive-deps

  const allMatches = matchData?.matches || [];
  const filtered   = sportFilter === 'all'
    ? allMatches
    : allMatches.filter(m => m.sport_code === sportFilter);
  const sports     = matchData?.sports || [];

  const enriched = filtered.map(m => {
    // Backend already returns correct my_players/opp_players arrays — just pass through
    return m;
  });

  if (loading) {
    return (
      <div className="p-3 space-y-2">
        {[1,2,3].map(i => <Skeleton key={i} className="h-10 bg-[#1d2230] rounded-xl"/>)}
      </div>
    );
  }

  return (
    <div className="border-t border-[#252a37] bg-[#0a0c14]">
      {/* Sport filter pills */}
      {sports.length > 1 && (
        <div className="flex gap-1.5 px-3 pt-2.5 pb-2 flex-wrap">
          <button onClick={() => setSportFilter('all')}
            className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
              sportFilter === 'all'
                ? 'bg-[#f5c518] text-[#0b0f18]'
                : 'bg-[#1d2230] text-[#8a93a3] border border-[#252a37] hover:text-[#f0f2f7]'
            }`}>
            All Sports
          </button>
          {sports.map(sc => (
            <button key={sc} onClick={() => setSportFilter(sc)}
              className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                sportFilter === sc
                  ? 'bg-[#f5c518] text-[#0b0f18]'
                  : 'bg-[#1d2230] text-[#8a93a3] border border-[#252a37] hover:text-[#f0f2f7]'
              }`}>
              <SportIcon sport={sc} size={12}/>
              {SPORT_LABELS[sc] || sc}
            </button>
          ))}
        </div>
      )}

      {/* Match list */}
      {enriched.length === 0 ? (
        <div className="px-4 py-6 text-center">
          <p className="text-[#f0f2f7] text-sm font-semibold">No individual match records</p>
          <p className="text-[10px] text-[#8a93a3] mt-1 max-w-xs mx-auto">
            Match history appears once matches are scored via the Scorer Portal with player names selected
          </p>
        </div>
      ) : (
        <div>
          {enriched.map((m, i) => <MatchRow key={i} m={m}/>)}
        </div>
      )}
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────
export default function PlayerScoresPage() {
  const { activeOrgSlug, user } = useAuthStore();
  const orgSlug = user?.memberships?.[0]?.slug || activeOrgSlug || 'btcc';
  const token   = () => localStorage.getItem('btccp_access_token');

  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [openId, setOpenId]   = useState(null); // expanded player_id

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`/api/orgs/${orgSlug}/rankings`,
        { headers: { Authorization: `Bearer ${token()}` } });
      if (res.ok) setData(await res.json());
      else toast.error('Failed to load players');
    } catch (_) { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [orgSlug]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchData(); }, [fetchData]);

  const rankings = data?.rankings || [];

  // Sort alphabetically A→Z and filter by search
  const filtered = rankings
    .slice()
    .sort((a, b) => (a.player_name || '').localeCompare(b.player_name || ''))
    .filter(p =>
      !search || (p.player_name || '').toLowerCase().includes(search.toLowerCase())
    );

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="player-scores-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-[rgba(34,197,94,0.1)] flex items-center justify-center">
            <Users className="h-5 w-5 text-[#22c55e]"/>
          </div>
          <div>
            <h1 className="text-2xl font-black text-[#f0f2f7]">Player Scores</h1>
            <p className="text-sm text-[#8a93a3]">
              {filtered.length} player{filtered.length !== 1 ? 's' : ''} · click to see match history
            </p>
          </div>
        </div>
        <button onClick={fetchData}
          className="p-2 rounded-lg text-[#8a93a3] hover:text-[#f0f2f7] hover:bg-[#161a26] transition-colors">
          <RefreshCw className="h-4 w-4"/>
        </button>
        <ExportBar
          label="Player Scores"
          onCSV={() => downloadCSV('player_scores',
            ['Player','Sport','Slot','Result','Score','Opponent','Team','Date'],
            (scores || []).flatMap(p => (p.matches || []).map(m => [
              p.player_name, m.sport_code, m.slot, m.result,
              m.my_score != null ? `${m.my_score}-${m.opp_score}` : '',
              m.opp_player || '', m.opp_team || '',
              m.scheduled_at ? new Date(m.scheduled_at).toLocaleDateString('en-IN') : '',
            ]))
          )}
          onPDF={printPage}
        />
      </div>

      {/* Search */}
      <div className="relative mb-5">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8a93a3]"/>
        <Input
          value={search}
          onChange={e => { setSearch(e.target.value); setOpenId(null); }}
          placeholder="Search player name…"
          className="pl-9 pr-8 bg-[#161a26] border-[#252a37] text-[#f0f2f7] placeholder:text-[#4a5568] focus:border-[#f5c518] focus-visible:ring-0"
          data-testid="player-search-input"
        />
        {search && (
          <button onClick={() => setSearch('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8a93a3] hover:text-[#f0f2f7]">
            <X className="h-3.5 w-3.5"/>
          </button>
        )}
      </div>

      {/* Player list */}
      {loading ? (
        <div className="space-y-2">
          {[1,2,3,4,5].map(i => <Skeleton key={i} className="h-16 bg-[#1d2230] rounded-xl"/>)}
        </div>
      ) : filtered.length === 0 ? (
        <Card className="bg-[#161a26] border-[#252a37]">
          <CardContent className="p-10 text-center">
            <Users className="h-10 w-10 text-[#8a93a3] mx-auto mb-3"/>
            <p className="text-[#f0f2f7] font-semibold">No players found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map((p, idx) => {
            const isOpen  = openId === p.player_id;
            const sports  = Object.keys(p.per_sport || {});
            const wins    = p.wins  || 0;
            const losses  = p.losses || 0;
            const played  = p.played || 0;
            const winPct  = played > 0 ? Math.round(wins / played * 100) : 0;
            const isPlat  = p.is_platinum;

            return (
              <Card key={p.player_id}
                className={`overflow-hidden transition-all ${
                  isOpen ? 'border-[rgba(34,197,94,0.3)]' : 'border-[#252a37]'
                } bg-[#10131c]`}>
                {/* Player row */}
                <button
                  data-testid={`player-row-${p.player_id}`}
                  className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-[#161a26] transition-colors"
                  onClick={() => setOpenId(isOpen ? null : p.player_id)}>

                  {/* Alpha badge */}
                  <div className="h-8 w-8 rounded-lg flex-shrink-0 flex items-center justify-center font-black text-sm bg-[rgba(138,147,163,0.08)] text-[#8a93a3]">
                    {(p.player_name || '?')[0].toUpperCase()}
                  </div>

                  {/* Name + sports */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <p className="text-sm font-semibold text-[#f0f2f7] truncate">{p.player_name}</p>
                      {isPlat && (
                        <span className="text-[9px] font-black text-[#f5c518] bg-[rgba(245,197,24,0.12)] px-1.5 py-0.5 rounded">★ PLAT</span>
                      )}
                    </div>
                    {sports.length > 0 && (
                      <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                        {sports.map(sc => (
                          <span key={sc} title={SPORT_LABELS[sc]}>
                            <SportIcon sport={sc} size={14}/>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* W / L / Win% */}
                  <div className="flex items-center gap-3 flex-shrink-0 text-center">
                    <div>
                      <p className="text-sm font-black text-[#22c55e] tabular-nums">{wins}</p>
                      <p className="text-[9px] text-[#8a93a3]">W</p>
                    </div>
                    <div>
                      <p className="text-sm font-black text-[#ef4444] tabular-nums">{losses}</p>
                      <p className="text-[9px] text-[#8a93a3]">L</p>
                    </div>
                    <div>
                      <p className="text-sm font-black text-[#f5c518] tabular-nums">{winPct}%</p>
                      <p className="text-[9px] text-[#8a93a3]">Win%</p>
                    </div>
                  </div>

                  {isOpen
                    ? <ChevronUp className="h-4 w-4 text-[#22c55e] flex-shrink-0"/>
                    : <ChevronDown className="h-4 w-4 text-[#8a93a3] flex-shrink-0"/>}
                </button>

                {/* Drill-down */}
                {isOpen && (
                  <PlayerDrilldown
                    player={p}
                    orgSlug={orgSlug}
                    token={token}
                  />
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
